import React, { useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, MenuItem, Stack, Table, TableBody, TableCell, TableRow, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import {
  ADJUSTMENT_LABELS, ADJUSTMENT_DESCRIPTIONS, checkAdjustmentAllowed,
  summarizeAdjustments, describePayable,
  type AdjustmentKind, type AdjustmentPolicy, type BackAdjustment,
} from '../utils/backAdjustment';

/**
 * バック調整のダイアログ。
 *
 * 自動計算値を上書きするのではなく、増減として積む形にしてある。
 * 「元がいくらで、何をいくら足し引きして、いまいくらか」が
 * 常に画面に出ているので、本人に聞かれてもそのまま説明できる。
 */

export interface BackAdjustmentDialogProps {
  open: boolean;
  onClose: () => void;
  staffId: string;
  staffName: string;
  salesPeriodKey: string;
  /** 自動計算されたバック額 */
  calculatedAmount: number;
  /** すでに積まれている調整 */
  existingAdjustments: BackAdjustment[];
  /** 日払いで渡した額（あれば） */
  alreadyPaidOut?: number;
  userRole: string;
  periodClosed?: boolean;
  policy?: Partial<AdjustmentPolicy>;
  onConfirm: (input: { kind: AdjustmentKind; delta: number; reason: string }) => Promise<void>;
  onReverse?: (adjustmentId: string) => Promise<void>;
}

const yen = (n: number) => `¥${Math.abs(Math.round(n)).toLocaleString()}`;
const signed = (n: number) => `${n >= 0 ? '+' : '−'}${yen(n)}`;

const BackAdjustmentDialog: React.FC<BackAdjustmentDialogProps> = ({
  open, onClose, staffName, salesPeriodKey, calculatedAmount, existingAdjustments,
  alreadyPaidOut, userRole, periodClosed, policy, onConfirm, onReverse,
}) => {
  const [kind, setKind] = useState<AdjustmentKind>('correction');
  const [direction, setDirection] = useState<'add' | 'subtract'>('add');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const delta = useMemo(() => {
    const n = Math.max(0, Number(amount) || 0);
    return direction === 'add' ? n : -n;
  }, [amount, direction]);

  const current = useMemo(
    () => summarizeAdjustments(calculatedAmount, existingAdjustments),
    [calculatedAmount, existingAdjustments]
  );
  const preview = useMemo(
    () => summarizeAdjustments(calculatedAmount, [
      ...existingAdjustments,
      { delta, kind, reason } as BackAdjustment,
    ]),
    [calculatedAmount, existingAdjustments, delta, kind, reason]
  );

  const check = useMemo(
    () => checkAdjustmentAllowed(
      { delta, calculatedAmount: current.payable, reason, kind },
      { userRole, periodClosed, alreadyPaidOut, policy }
    ),
    [delta, current.payable, reason, kind, userRole, periodClosed, alreadyPaidOut, policy]
  );

  const canSubmit = check.allowed && delta !== 0 && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm({ kind, delta, reason: reason.trim() });
      setAmount('');
      setReason('');
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const live = existingAdjustments.filter((a) => !a.reversedByAdjustmentId);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        バックを調整する
        <Typography variant="body2" color="text.secondary">
          {staffName} / {salesPeriodKey} 期
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>自動計算の値は書き換えません</AlertTitle>
          調整は増減として積まれます。元の額と、何をいくら足し引きしたかが常に残るので、
          本人から聞かれたときにそのまま説明できます。
        </Alert>

        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ border: 0, pl: 0 }}>自動計算</TableCell>
              <TableCell align="right" sx={{ border: 0, pr: 0, fontFamily: 'monospace' }}>
                {yen(current.calculated)}
              </TableCell>
            </TableRow>
            {current.breakdown.map((b) => (
              <TableRow key={b.kind}>
                <TableCell sx={{ border: 0, pl: 0, color: 'text.secondary' }}>
                  {b.label}（{b.count}件）
                </TableCell>
                <TableCell align="right" sx={{ border: 0, pr: 0, fontFamily: 'monospace', color: b.total >= 0 ? 'success.main' : 'error.main' }}>
                  {signed(b.total)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={{ pl: 0, fontWeight: 'bold' }}>現在の支給額</TableCell>
              <TableCell align="right" sx={{ pr: 0, fontFamily: 'monospace', fontWeight: 'bold' }}>
                {yen(current.payable)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {live.length > 0 && onReverse && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">積まれている調整</Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {live.map((a) => (
                <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip size="small" label={ADJUSTMENT_LABELS[a.kind]} />
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', minWidth: 90 }}>
                    {signed(a.delta)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {a.reason}
                  </Typography>
                  <Button size="small" onClick={() => a.id && onReverse(a.id)}>
                    打ち消す
                  </Button>
                </Box>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              「打ち消す」は記録を消しません。逆向きの調整を積んで相殺します。
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>新しい調整</Typography>

        <TextField
          select fullWidth size="small" label="種類" value={kind}
          onChange={(e) => setKind(e.target.value as AdjustmentKind)}
          sx={{ mb: 1 }}
        >
          {(Object.keys(ADJUSTMENT_LABELS) as AdjustmentKind[]).map((k) => (
            <MenuItem key={k} value={k}>{ADJUSTMENT_LABELS[k]}</MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {ADJUSTMENT_DESCRIPTIONS[kind]}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <ToggleButtonGroup
            exclusive size="small" value={direction}
            onChange={(_, v) => v && setDirection(v)}
          >
            <ToggleButton value="add">足す</ToggleButton>
            <ToggleButton value="subtract">引く</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            type="number" size="small" label="金額" value={amount}
            onChange={(e) => setAmount(e.target.value)} sx={{ flex: 1 }}
          />
        </Stack>

        <TextField
          fullWidth multiline rows={2} size="small" label="理由（必須）"
          value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="例: 9/14 のシャンパン配分が誤っていたため訂正"
          sx={{ mb: 2 }}
        />

        {delta !== 0 && (
          <Alert severity="success" icon={false} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {yen(current.payable)} {signed(delta)} = <b>{yen(preview.payable)}</b>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {describePayable(preview)}
            </Typography>
          </Alert>
        )}

        {check.blockers.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {check.blockers.join('\n')}
          </Alert>
        )}
        {check.warnings.length > 0 && (
          <Alert severity="warning" sx={{ whiteSpace: 'pre-line' }}>
            {check.warnings.join('\n\n')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>やめる</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!canSubmit}>
          調整を記録する
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BackAdjustmentDialog;
